import React, { useState } from 'react';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { CText } from '../../../components/common';

interface EvaluationStep {
  step_index: number;
  agent: string;
  label: string;
  status: 'completed' | 'skipped' | 'error';
  summary: string;
  details: Record<string, any>;
}

interface EvaluationTraceProps {
  steps: EvaluationStep[];
  colors: any;
}

const dotColor = (status: string, colors: any): string => {
  switch (status) {
    case 'completed': return colors.primary || '#6B39F4';
    case 'skipped': return colors.grayScale400 || '#9ca3af';
    case 'error': return '#ef4444';
    default: return colors.primary || '#6B39F4';
  }
};

const formatDetailValue = (value: any): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    if (value.length === 0) return '-';
    return value.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ');
  }
  if (typeof value === 'number') {
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(2);
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const formatDetailKey = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

const StepRow: React.FC<{ step: EvaluationStep; index: number; isLast: boolean; colors: any }> = ({ step, index, isLast, colors }) => {
  const [expanded, setExpanded] = useState(false);
  const detailEntries = Object.entries(step.details || {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
  );
  const hasDetails = detailEntries.length > 0;
  const dot = dotColor(step.status, colors);
  const lineColor = (colors.primary || '#6B39F4') + '30';

  return (
    <div
      className="animate-fade-in-up"
      style={{ animationDelay: `${index * 60}ms`, animationFillMode: 'both' }}
    >
      <div className="flex" style={{ minHeight: 36 }}>
        {/* Timeline column */}
        <div className="flex flex-col items-center" style={{ width: 20, flexShrink: 0 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: dot,
              marginTop: 5,
              flexShrink: 0,
            }}
          />
          {!isLast && (
            <div style={{ width: 2, flex: 1, backgroundColor: lineColor, marginTop: 2, marginBottom: 2 }} />
          )}
        </div>

        {/* Content column */}
        <div className="flex-1 min-w-0 pb-2" style={{ paddingLeft: 6 }}>
          <button
            onClick={() => hasDetails && setExpanded(!expanded)}
            className="w-full text-left"
            style={{ cursor: hasDetails ? 'pointer' : 'default' }}
          >
            <CText type="r12" color={colors.textColor} style={{ fontWeight: 600 }}>
              {step.label}
            </CText>
            <div style={{ marginTop: 1 }}>
              <CText type="r12" color={colors.grayScale500} style={{ lineHeight: 1.4 }}>
                {step.summary}
              </CText>
            </div>
          </button>

          {expanded && hasDetails && (
            <div
              className="mt-1.5 px-2.5 py-1.5 rounded"
              style={{ backgroundColor: colors.backgroundColor + '80', border: `1px solid ${colors.borderColor}` }}
            >
              {detailEntries.map(([key, value]) => (
                <div key={key} className="flex gap-2 py-0.5">
                  <CText type="r10" color={colors.grayScale400} style={{ whiteSpace: 'nowrap', minWidth: 90 }}>
                    {formatDetailKey(key)}:
                  </CText>
                  <CText type="r10" color={colors.grayScale500} style={{ wordBreak: 'break-word' }}>
                    {formatDetailValue(value)}
                  </CText>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EvaluationTrace: React.FC<EvaluationTraceProps> = ({ steps, colors }) => {
  const [open, setOpen] = useState(true);

  if (!steps || steps.length === 0) return null;

  const accentColor = (colors.primary || '#6B39F4') + '40';

  return (
    <div className="mb-2">
      {/* Header */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full text-left px-3 py-1.5 rounded-lg transition-colors"
        style={{
          backgroundColor: colors.inputBackground,
          border: `1px solid ${colors.borderColor}`,
        }}
      >
        <div
          style={{
            width: 6,
            height: 6,
            borderRadius: 2,
            backgroundColor: colors.primary || '#6B39F4',
            transform: 'rotate(45deg)',
            flexShrink: 0,
          }}
        />
        <CText type="r12" color={colors.grayScale500} style={{ flex: 1, fontWeight: 500 }}>
          Analyzed in {steps.length} step{steps.length !== 1 ? 's' : ''}
        </CText>
        <span style={{ flexShrink: 0 }}>
          {open
            ? React.createElement(IoChevronUp as any, { size: 12, color: colors.grayScale400 })
            : React.createElement(IoChevronDown as any, { size: 12, color: colors.grayScale400 })
          }
        </span>
      </button>

      {/* Accent line */}
      <div style={{ height: 2, backgroundColor: accentColor, borderRadius: 1, marginTop: -1 }} />

      {/* Steps timeline */}
      {open && (
        <div className="mt-2 pl-2 pt-1">
          {steps.map((step, i) => (
            <StepRow
              key={step.step_index}
              step={step}
              index={i}
              isLast={i === steps.length - 1}
              colors={colors}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default EvaluationTrace;
